import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { API_BASE } from "../../utils/api";
import { formatPrice, formatArea, trustColor } from "../../utils/format";
import { usePageTitle } from "../../hooks/usePageTitle";
import { InquiryModal } from "../inquiries";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useFavorites } from "../../hooks/useFavorites";
import { AppHeader } from "../../components/AppHeader";
import { AppFooter } from "../../components/AppFooter";
import { LandTransactionSection } from "./LandTransactionSection";
import { LandUseSection } from "./LandUseSection";
import { useLiveTour } from "../live-tour/useLiveTour";
import { generateConsultId } from "../../services/rtdb";
import { ListingAIReport } from "./ListingAIReport";

declare global {
  interface Window { kakao: any; __kakaoMapLoading?: boolean; }
}

const KAKAO_KEY = import.meta.env.VITE_KAKAO_MAP_KEY;

interface ListingDetail {
  id: string;
  broker_id?: string;
  broker_name: string;
  broker_phone?: string;
  broker_office_name?: string;
  broker_trust_score?: number;
  broker_is_verified?: boolean;
  property_type: string;
  region_sido: string;
  region_sigungu: string;
  region_eupmyeondong: string;
  blind_lat: number;
  blind_lng: number;
  area_pyeong: number;
  price_manwon: number;
  land_category: string;
  zoning: string;
  road_access: string;
  water_supply: string;
  recommended_use: string;
  description: string;
  status: string;
  view_count: number;
  nda_request_count: number;
  created_at: string;
  nda_agreed?: boolean;
  exact_address?: string;
  exact_lat?: number;
  exact_lng?: number;
  owner_contact?: string;
  detailed_notes?: string;
  photos?: string[];
}

// 유형별 색상
const TYPE_COLORS: Record<string, string> = {
  "\uB18D\uC9C0": "#16A34A", "\uC784\uC57C": "#92400E",
  "\uACF5\uC7A5\uC6A9\uC9C0": "#6B7280", "\uCC3D\uACE0\uC6A9\uC9C0": "#6B7280", "\uACF5\uC7A5": "#6B7280", "\uCC3D\uACE0": "#6B7280",
  "\uC0C1\uAC00": "#3B82F6", "\uC8FC\uD0DD": "#3B82F6", "\uBBD8\uD130": "#8B5CF6",
};

// 유형별 아이콘
const TYPE_ICONS: Record<string, string> = {
  "\uB18D\uC9C0": "\uD83C\uDF3E", "\uC784\uC57C": "\uD83C\uDF32",
  "\uACF5\uC7A5": "\uD83C\uDFED", "\uCC3D\uACE0": "\uD83D\uDCE6",
  "\uC0C1\uAC00": "\uD83C\uDFEA", "\uC8FC\uD0DD": "\uD83C\uDFE0", "\uBBD8\uD130": "\uD83E\uDEA6",
};

function loadKakaoSDK(): Promise<boolean> {
  return new Promise((resolve) => {
    if (window.kakao?.maps?.LatLng) { resolve(true); return; }
    if (!KAKAO_KEY) { resolve(false); return; }
    if (window.__kakaoMapLoading) {
      const id = setInterval(() => {
        if (window.kakao?.maps?.LatLng) { clearInterval(id); resolve(true); }
      }, 200);
      return;
    }
    window.__kakaoMapLoading = true;
    const s = document.createElement("script");
    s.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${KAKAO_KEY}&autoload=false`;
    s.onload = () => window.kakao.maps.load(() => { window.__kakaoMapLoading = false; resolve(true); });
    s.onerror = () => { window.__kakaoMapLoading = false; resolve(false); };
    document.head.appendChild(s);
  });
}

export function ListingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const m = useIsMobile();

  const { isFav, toggle: toggleFav } = useFavorites();
  const tour = useLiveTour(id ?? "");
  const [listing, setListing] = useState<ListingDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const [showInquiry, setShowInquiry] = useState(false);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const st = styles(m);
  const isLoggedIn = !!localStorage.getItem("broker_token");

  // 동적 페이지 타이틀
  usePageTitle(
    listing
      ? `${listing.region_eupmyeondong} ${listing.area_pyeong}\uD3C9 ${formatPrice(listing.price_manwon)}\uC6D0`
      : "\uB9E4\uBB3C \uC0C1\uC138"
  );

  useEffect(() => {
    if (!id) return;
    const token = localStorage.getItem("broker_token");
    const headers: Record<string, string> = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    fetch(`${API_BASE}/api/listings/${id}`, { headers })
      .then((r) => { if (!r.ok) throw new Error("\uB9E4\uBB3C\uC744 \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4"); return r.json(); })
      .then(setListing)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  // 지도 표시
  useEffect(() => {
    if (!listing || !mapContainerRef.current) return;
    const useExact = listing.nda_agreed && listing.exact_lat && listing.exact_lng;
    const lat = useExact ? listing.exact_lat! : listing.blind_lat;
    const lng = useExact ? listing.exact_lng! : listing.blind_lng;
    if (!lat || !lng) return;

    const isMyon = listing.region_eupmyeondong?.endsWith("\uBA74"); // "면" 판별
    const blindRadius = isMyon ? 3000 : 1000;
    const blindLevel = isMyon ? 8 : 6;

    loadKakaoSDK().then((ok) => {
      if (!ok || !mapContainerRef.current) return;
      const kakao = window.kakao;
      const center = new kakao.maps.LatLng(lat, lng);
      const map = new kakao.maps.Map(mapContainerRef.current, { center, level: useExact ? 4 : blindLevel });

      if (!useExact) {
        new kakao.maps.Circle({
          map, center, radius: blindRadius,
          strokeWeight: 2, strokeColor: "#D4AF37", strokeOpacity: 0.6,
          fillColor: "#D4AF37", fillOpacity: 0.08,
        });
      }

      const marker = new kakao.maps.Marker({ position: center, map });
      const iwContent = useExact
        ? `<div style="padding:8px 14px;font-size:13px;font-family:-apple-system,sans-serif;"><b style="color:#16A34A;">\uC815\uD655\uD55C \uC704\uCE58</b><div style="color:#666;font-size:12px;margin-top:2px;">${listing.exact_address || ""}</div></div>`
        : `<div style="padding:8px 14px;font-size:13px;font-family:-apple-system,sans-serif;"><b style="color:#D4AF37;">\uB300\uB7B5\uC801 \uC704\uCE58</b><div style="color:#666;font-size:12px;margin-top:2px;">${listing.region_sigungu} ${listing.region_eupmyeondong} \uC77C\uB300</div></div>`;
      const iw = new kakao.maps.InfoWindow({ content: iwContent });
      iw.open(map, marker);
    });
  }, [listing]);

  if (loading) return (
    <div style={st.page}><div style={st.loadingWrap}><div style={st.spinner} /><span style={{ color: "var(--text-muted)", fontSize: 14 }}>{"\uBD88\uB7EC\uC624\uB294 \uC911..."}</span></div></div>
  );

  if (error || !listing) return (
    <div style={st.page}><div style={st.errorWrap}><div style={{ fontSize: 48, marginBottom: 16 }}>{"\uD83D\uDDFA\uFE0F"}</div><div style={{ fontSize: 16, color: "var(--text-secondary)", marginBottom: 20 }}>{error || "\uB9E4\uBB3C\uC744 \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4"}</div><button onClick={() => navigate("/listings")} style={st.backBtnLarge}>{"\u2190 \uBAA9\uB85D\uC73C\uB85C"}</button></div></div>
  );

  const typeColor = TYPE_COLORS[listing.property_type] || "#6B7280";
  const typeIcon = TYPE_ICONS[listing.property_type] || "\uD83D\uDCCD";
  const pricePerPyeong = listing.area_pyeong > 0 ? Math.round(listing.price_manwon / listing.area_pyeong) : 0;
  const photos = listing.photos && listing.photos.length > 0 ? listing.photos : null;

  const infoCards = [
    { icon: typeIcon, label: "\uB9E4\uBB3C \uC720\uD615", value: listing.property_type },
    { icon: "\uD83D\uDCCF", label: "\uBA74\uC801", value: formatArea(listing.area_pyeong) },
    { icon: "\uD83D\uDCB0", label: "\uB9E4\uB9E4\uAC00", value: `${formatPrice(listing.price_manwon)}\uC6D0` },
    { icon: "\uD83D\uDCCA", label: "\uD3C9\uB2F9\uAC00", value: pricePerPyeong > 0 ? `${pricePerPyeong.toLocaleString()}\uB9CC\uC6D0` : "-" },
    ...(listing.zoning ? [{ icon: "\uD83D\uDCCB", label: "\uC6A9\uB3C4\uC9C0\uC5ED", value: listing.zoning }] : []),
    ...(listing.road_access ? [{ icon: "\uD83D\uDEE3\uFE0F", label: "\uB3C4\uB85C\uC811\uBA74", value: listing.road_access }] : []),
    ...(listing.water_supply ? [{ icon: "\uD83D\uDCA7", label: "\uC218\uB3C4/\uC6A9\uC218", value: listing.water_supply }] : []),
    ...(listing.recommended_use ? [{ icon: "\u2B50", label: "\uCD94\uCC9C \uC6A9\uB3C4", value: listing.recommended_use }] : []),
  ];

  return (
    <div style={st.page}>
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}} @keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <AppHeader backTo="/listings" backLabel="← 목록" />

      <div style={st.content}>
        {/* ===== 1. 상단 히어로 ===== */}
        <div style={st.heroCard}>
          <div style={st.heroTop}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ ...st.typeBadge, background: typeColor }}>{listing.property_type}</span>
              {tour.isLive && (
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  background: "#dc2626", color: "#fff",
                  borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 700,
                  animation: "pulse 1.5s ease infinite",
                }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff", display: "inline-block" }} />
                  LIVE
                </span>
              )}
            </div>
            <span style={st.heroDate}>{listing.created_at.slice(0, 10)} {"\uB4F1\uB85D"}</span>
          </div>
          <h1 style={st.heroTitle}>{listing.region_sido} {listing.region_sigungu} {listing.region_eupmyeondong}</h1>
          <div style={st.heroPriceRow}>
            <span style={st.heroPrice}>{formatPrice(listing.price_manwon)}<span style={st.heroPriceUnit}>{"\uC6D0"}</span></span>
          </div>
          <div style={st.heroMeta}>
            <span style={st.heroMetaItem}>{"\uD83D\uDCCF"} {formatArea(listing.area_pyeong)}</span>
            <span style={st.heroMetaDivider}>{"\u00B7"}</span>
            <span style={st.heroMetaItem}>{"\uD83D\uDCCA"} {"\uD3C9\uB2F9"} {pricePerPyeong > 0 ? `${pricePerPyeong.toLocaleString()}\uB9CC\uC6D0` : "-"}</span>
            <span style={st.heroMetaDivider}>{"\u00B7"}</span>
            <span style={st.heroMetaItem}>{"\uD83D\uDC41"} {"\uC870\uD68C"} {listing.view_count}</span>
          </div>
          <button
            onClick={() => toggleFav(listing.id)}
            style={isFav(listing.id) ? st.favBtnActive : st.favBtn}
          >
            {isFav(listing.id) ? "\u2665 \uAD00\uC2EC\uB9E4\uBB3C \uB4F1\uB85D\uB428" : "\u2661 \uAD00\uC2EC\uB9E4\uBB3C \uB4F1\uB85D"}
          </button>
        </div>

        {/* ===== 2. 사진 갤러리 ===== */}
        <div style={st.section}>
          {photos ? (
            <>
              <div style={st.galleryMain} onClick={() => setLightboxIdx(0)}>
                <img src={photos[0]} alt={"\uB9E4\uBB3C \uC0AC\uC9C4"} style={st.galleryMainImg} />
              </div>
              {photos.length > 1 && (
                <div style={st.galleryThumbs}>
                  {photos.slice(1, 5).map((url, i) => (
                    <div key={i} style={st.thumbWrap} onClick={() => setLightboxIdx(i + 1)}>
                      <img src={url} alt={`\uC0AC\uC9C4 ${i + 2}`} style={st.thumbImg} />
                    </div>
                  ))}
                  {photos.length > 5 && (
                    <div style={st.thumbMore} onClick={() => setLightboxIdx(5)}>+{photos.length - 5}</div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div style={st.noPhoto}>
              <div style={st.noPhotoIcon}>{typeIcon}</div>
              <div style={st.noPhotoText}>{"\uC0AC\uC9C4 \uC900\uBE44\uC911"}</div>
              <div style={st.noPhotoSub}>{"\uB4F1\uB85D\uB41C \uC0AC\uC9C4\uC774 \uC5C6\uC2B5\uB2C8\uB2E4"}</div>
            </div>
          )}
        </div>

        {/* ===== 3. 핵심 정보 그리드 ===== */}
        <div style={st.section}>
          <h2 style={st.sectionTitle}>{"\uD575\uC2EC \uC815\uBCF4"}</h2>
          <div style={st.infoGrid}>
            {infoCards.map((c) => (
              <div key={c.label} style={st.infoCell}>
                <div style={st.infoCellIcon}>{c.icon}</div>
                <div>
                  <div style={st.infoCellLabel}>{c.label}</div>
                  <div style={st.infoCellValue}>{c.value}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ===== 4. 위치 정보 ===== */}
        <div style={st.section}>
          <h2 style={st.sectionTitle}>{listing.nda_agreed ? "\uD83D\uDCCD \uC815\uD655\uD55C \uC704\uCE58" : "\uD83D\uDCCD \uC704\uCE58 \uC815\uBCF4"}</h2>
          {listing.nda_agreed && listing.exact_address && (
            <div style={st.exactAddr}>{listing.exact_address}</div>
          )}
          <div style={st.mapWrap}>
            <div ref={mapContainerRef} style={st.map} />
            {!listing.nda_agreed && (
              <div style={st.blindBanner}>
                {"\uD83D\uDCCD \uC774 \uC9C0\uB3C4\uB294 \uB300\uB7B5\uC801\uC778 \uC704\uCE58\uB97C \uD45C\uC2DC\uD569\uB2C8\uB2E4. \uC815\uD655\uD55C \uC8FC\uC18C\uB294 \uBE44\uBC00\uC720\uC9C0\uACC4\uC57D(NDA) \uB3D9\uC758 \uD6C4 \uD655\uC778\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4."}
              </div>
            )}
            {listing.nda_agreed && (
              <div style={st.exactBanner}>
                {"\uD83D\uDCCD \uC815\uD655\uD55C \uC704\uCE58\uAC00 \uD45C\uC2DC\uB418\uC5B4 \uC788\uC2B5\uB2C8\uB2E4."}
              </div>
            )}
          </div>
          {!listing.nda_agreed && (
            <div style={st.mapCaption}>{listing.region_sigungu} {listing.region_eupmyeondong} {listing.region_eupmyeondong?.endsWith("\uBA74") ? "\uC77C\uB300 (\uBC18\uACBD 3km \uB0B4)" : "\uC77C\uB300 (\uBC18\uACBD 1km \uB0B4)"}</div>
          )}
        </div>

        {/* ===== 5. 상세 설명 ===== */}
        {(listing.description || (listing.nda_agreed && listing.detailed_notes)) && (
          <div style={st.section}>
            <h2 style={st.sectionTitle}>{"\uC0C1\uC138 \uC124\uBA85"}</h2>
            {listing.description && <p style={st.descText}>{listing.description}</p>}
            {listing.nda_agreed && listing.detailed_notes && (
              <>
                <div style={st.noteDivider} />
                <div style={st.noteLabel}>{"\uD83D\uDD12 NDA \uBE44\uACF5\uAC1C \uBA54\uBAA8"}</div>
                <p style={st.descText}>{listing.detailed_notes}</p>
              </>
            )}
          </div>
        )}

        {/* ===== NDA 동의 시: 소유자 정보 ===== */}
        {listing.nda_agreed && listing.owner_contact && (
          <div style={st.section}>
            <h2 style={st.sectionTitle}>{"\uD83D\uDD13 \uC18C\uC720\uC790 \uC5F0\uB77D\uCC98"}</h2>
            <div style={st.ownerCard}>
              <div style={st.ownerIcon}>{"\uD83D\uDC64"}</div>
              <div style={st.ownerContact}>{listing.owner_contact}</div>
            </div>
          </div>
        )}

        {/* ===== 6. NDA 미동의 시 하단 CTA ===== */}
        {!listing.nda_agreed && (
          <div style={st.ctaCard}>
            <div style={st.ctaIcon}>{"\uD83D\uDD10"}</div>
            <div style={st.ctaTextWrap}>
              <div style={st.ctaTitle}>{"\uC815\uD655\uD55C \uC704\uCE58\uC640 \uC18C\uC720\uC790 \uC815\uBCF4\uB97C \uD655\uC778\uD558\uC138\uC694"}</div>
              <div style={st.ctaSub}>{"NDA(\uBE44\uBC00\uC720\uC9C0\uACC4\uC57D) \uB3D9\uC758 \uD6C4 \uC815\uD655\uD55C \uC8FC\uC18C, \uC9C0\uBC88, \uC18C\uC720\uC790 \uC5F0\uB77D\uCC98\uB97C \uC5F4\uB78C\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4."}</div>
            </div>
            {!isLoggedIn ? (
              <Link to="/login" style={st.ctaBtn}>{"\uB85C\uADF8\uC778 \uD6C4 NDA \uB3D9\uC758"}</Link>
            ) : (
              <button style={st.ctaBtn} onClick={() => navigate(`/nda?redirect=/listings/${id}`)}>{"\uD83D\uDD12 NDA \uB3D9\uC758\uD558\uAE30"}</button>
            )}
          </div>
        )}

        {/* ===== 7. AI 분석 리포트 ===== */}
        <ListingAIReport listingId={listing.id} />

        {/* ===== 8. 주변 실거래 시세 ===== */}
        <LandTransactionSection
          sido={listing.region_sido}
          sigungu={listing.region_sigungu}
          isMobile={m}
        />

        {/* ===== 9. 토지이용계획 (NDA 동의 후) ===== */}
        {listing.nda_agreed && listing.exact_address && (
          <LandUseSection
            listingId={listing.id}
            landCategory={listing.land_category || ""}
            zoning={listing.zoning || ""}
            exactAddress={listing.exact_address}
            isMobile={m}
          />
        )}

        {/* ===== 10. 중개사 정보 & 문의 ===== */}
        <div style={st.section}>
          <h2 style={st.sectionTitle}>{"\uB2F4\uB2F9 \uC911\uAC1C\uC0AC"}</h2>
          <div style={st.brokerCard}>
            <div style={st.brokerAvatar}>{listing.broker_name?.charAt(0)}</div>
            <div style={{ flex: 1 }}>
              <div style={st.brokerName}>{listing.broker_name}</div>
              {listing.broker_office_name && <div style={st.brokerOffice}>{listing.broker_office_name}</div>}
            </div>
            {listing.broker_trust_score !== undefined && (
              <span style={{ padding: "4px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600, background: `${trustColor(listing.broker_trust_score)}20`, color: trustColor(listing.broker_trust_score) }}>
                {"\uC2E0\uB8B0"} {listing.broker_trust_score}{"\uC810"}
              </span>
            )}
          </div>
          <div style={st.inquiryBtns}>
            {listing.broker_phone && (
              <a href={`tel:${listing.broker_phone}`} style={st.phoneBtn}>
                {"\uD83D\uDCDE \uC804\uD654 \uBB38\uC758"}
              </a>
            )}
            <button onClick={() => setShowInquiry(true)} style={st.inquiryBtn}>
              {"\uD83D\uDCE8 \uBB38\uC758 \uBA54\uC2DC\uC9C0"}
            </button>
          </div>

          {/* Phase 6: 라이브 투어 & 화상 상담 */}
          <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
            {/* 라이브 투어 시청 (라이브 중일 때) */}
            {tour.isLive && (
              <button
                onClick={() => navigate(`/listings/${id}/live`)}
                style={{
                  width: "100%", padding: "12px", borderRadius: 10, border: "none",
                  background: "linear-gradient(135deg, #dc2626, #b91c1c)",
                  color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}
              >
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#fff", display: "inline-block", animation: "pulse 1s infinite" }} />
                {"\uB77C\uC774\uBE0C \uD22C\uC5B4 \uC2DC\uCCAD"} ({tour.viewerCount}{"\uBA85 \uC2DC\uCCAD \uC911"})
              </button>
            )}

            {/* 라이브 시작 (로그인한 중개사용) */}
            {isLoggedIn && !tour.isLive && (
              <button
                onClick={() => navigate(`/listings/${id}/broadcast`)}
                style={{
                  width: "100%", padding: "10px", borderRadius: 10,
                  border: "2px dashed #374151",
                  background: "transparent",
                  color: "#64748b", fontWeight: 600, fontSize: 13, cursor: "pointer",
                }}
              >
                {"\uD83D\uDCF9 \uB77C\uC774\uBE0C \uD22C\uC5B4 \uC2DC\uC791 (\uC911\uAC1C\uC0AC \uC804\uC6A9)"}
              </button>
            )}

            {/* 화상 상담 (NDA 동의 + 로그인) */}
            {listing.nda_agreed && isLoggedIn && (
              <button
                onClick={() => {
                  const cid = generateConsultId();
                  navigate(`/consult/${cid}?role=investor`);
                }}
                style={{
                  width: "100%", padding: "12px", borderRadius: 10, border: "none",
                  background: "linear-gradient(135deg, #1d4ed8, #7c3aed)",
                  color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer",
                }}
              >
                {"\uD83D\uDCF9 \uD654\uC0C1 \uC0C1\uB2F4 \uC694\uCCAD"}
              </button>
            )}
          </div>
        </div>

        {/* 푸터 메타 */}
        <div style={st.footerMeta}>
          <span>{"\uB2F4\uB2F9 \uC911\uAC1C\uC0AC: "}{listing.broker_name}</span>
          <span>{"\uB4F1\uB85D\uC77C: "}{listing.created_at.slice(0, 10)}</span>
          <span>{"NDA \uC694\uCCAD: "}{listing.nda_request_count}{"\uAC74"}</span>
        </div>
      </div>

      {/* ===== 문의 모달 ===== */}
      {showInquiry && (
        <InquiryModal
          listingId={listing.id}
          brokerName={listing.broker_name}
          onClose={() => setShowInquiry(false)}
          onSuccess={() => { setShowInquiry(false); alert("\uBB38\uC758\uAC00 \uC804\uC1A1\uB418\uC5C8\uC2B5\uB2C8\uB2E4."); }}
        />
      )}

      {/* ===== 라이트박스 ===== */}
      {lightboxIdx !== null && photos && (
        <div style={st.lightbox} onClick={() => setLightboxIdx(null)}>
          <button style={st.lbClose} onClick={() => setLightboxIdx(null)}>{"\u00D7"}</button>
          <img src={photos[lightboxIdx]} alt="" style={st.lbImg} onClick={(e) => e.stopPropagation()} />
          <div style={st.lbNav} onClick={(e) => e.stopPropagation()}>
            <button style={st.lbArrow} disabled={lightboxIdx === 0} onClick={() => setLightboxIdx(Math.max(0, lightboxIdx - 1))}>{"\u2190"}</button>
            <span style={{ color: "#fff", fontSize: 14 }}>{lightboxIdx + 1} / {photos.length}</span>
            <button style={st.lbArrow} disabled={lightboxIdx === photos.length - 1} onClick={() => setLightboxIdx(Math.min(photos.length - 1, lightboxIdx + 1))}>{"\u2192"}</button>
          </div>
        </div>
      )}
      <AppFooter />
    </div>
  );
}

/* ───── Styles ───── */
const gold = "#D4AF37";

function styles(m: boolean): Record<string, React.CSSProperties> {
  return {
    /* Page */
    page: { minHeight: "100vh", background: "var(--bg-primary)" },
    loadingWrap: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, minHeight: "60vh" },
    spinner: { width: 32, height: 32, border: `3px solid var(--border-light)`, borderTopColor: gold, borderRadius: "50%", animation: "spin 0.8s linear infinite" },
    errorWrap: { textAlign: "center" as const, padding: m ? "60px 16px" : "100px 20px" },
    backBtnLarge: { padding: "12px 28px", borderRadius: 8, border: `1px solid var(--border-medium)`, background: "transparent", color: "var(--text-secondary)", fontSize: 14, cursor: "pointer" },

    /* Header */
    header: { background: "var(--bg-card)", borderBottom: "1px solid var(--border-light)", position: "sticky" as const, top: 0, zIndex: 100 },
    headerInner: { maxWidth: 1200, margin: "0 auto", padding: m ? "0 16px" : "0 32px", height: m ? 48 : 56, display: "flex", alignItems: "center", justifyContent: "space-between" },
    headerBack: { background: "none", border: "none", color: "var(--text-secondary)", fontSize: m ? 13 : 14, cursor: "pointer", fontWeight: 500 },
    logo: { fontSize: m ? 16 : 18, fontWeight: 800, color: gold, cursor: "pointer", letterSpacing: "-0.5px" },
    nav: { display: "flex", gap: 12 },
    navLink: { fontSize: m ? 13 : 14, color: "var(--text-secondary)", textDecoration: "none", fontWeight: 500 },
    content: { maxWidth: 1200, margin: "0 auto", padding: m ? "16px 16px 40px" : "24px 32px 60px", display: "flex", flexDirection: "column" as const, gap: m ? 14 : 20 },

    /* Hero */
    heroCard: { background: "var(--bg-card)", border: "1px solid var(--border-light)", borderRadius: m ? 12 : 16, padding: m ? "20px 16px" : "28px 32px" },
    heroTop: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: m ? 8 : 12 },
    typeBadge: { display: "inline-block", padding: m ? "3px 10px" : "4px 14px", borderRadius: 20, color: "#fff", fontSize: m ? 12 : 13, fontWeight: 700, letterSpacing: "0.02em" },
    heroDate: { fontSize: m ? 12 : 13, color: "var(--text-muted)" },
    heroTitle: { fontSize: m ? 19 : 24, fontWeight: 800, color: "var(--text-primary)", margin: "0 0 10px", lineHeight: 1.3 },
    heroPriceRow: { marginBottom: m ? 6 : 10 },
    heroPrice: { fontSize: m ? 24 : 32, fontWeight: 800, color: "var(--text-primary)" },
    heroPriceUnit: { fontSize: m ? 14 : 16, fontWeight: 400, color: "var(--text-muted)", marginLeft: 4 },
    heroMeta: { display: "flex", alignItems: "center", gap: m ? 6 : 8, flexWrap: "wrap" as const },
    heroMetaItem: { fontSize: m ? 13 : 14, color: "var(--text-secondary)" },
    heroMetaDivider: { color: "var(--border-medium)", fontSize: 14 },

    /* Photo gallery */
    galleryMain: { borderRadius: 12, overflow: "hidden" as const, cursor: "pointer", maxHeight: m ? 240 : 400 },
    galleryMainImg: { width: "100%", height: "100%", objectFit: "cover" as const, display: "block", maxHeight: m ? 240 : 400 },
    galleryThumbs: { display: "flex", gap: m ? 6 : 8, marginTop: 8 },
    thumbWrap: { flex: "1 1 0", maxWidth: m ? "calc(25% - 5px)" : 100, height: m ? 56 : 72, borderRadius: 8, overflow: "hidden" as const, cursor: "pointer", border: "1px solid var(--border-light)" },
    thumbImg: { width: "100%", height: "100%", objectFit: "cover" as const },
    thumbMore: { flex: "1 1 0", maxWidth: m ? "calc(25% - 5px)" : 100, height: m ? 56 : 72, borderRadius: 8, background: "var(--bg-secondary)", border: "1px solid var(--border-light)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: m ? 14 : 16, fontWeight: 700, color: "var(--text-muted)", cursor: "pointer" },
    noPhoto: { background: "var(--bg-card)", border: "1px solid var(--border-light)", borderRadius: m ? 12 : 16, padding: m ? "32px 16px" : "48px 20px", textAlign: "center" as const },
    noPhotoIcon: { fontSize: m ? 40 : 56, marginBottom: 12, opacity: 0.5 },
    noPhotoText: { fontSize: m ? 15 : 16, fontWeight: 700, color: "var(--text-secondary)", marginBottom: 4 },
    noPhotoSub: { fontSize: 13, color: "var(--text-muted)" },

    /* Section common */
    section: { background: "var(--bg-card)", border: "1px solid var(--border-light)", borderRadius: m ? 12 : 16, padding: m ? "18px 14px" : "24px 28px" },
    sectionTitle: { fontSize: m ? 15 : 16, fontWeight: 700, color: "var(--text-primary)", margin: m ? "0 0 12px" : "0 0 16px" },

    /* Info grid */
    infoGrid: { display: "grid", gridTemplateColumns: m ? "1fr" : "1fr 1fr", gap: m ? 8 : 12 },
    infoCell: { display: "flex", alignItems: "center", gap: m ? 10 : 12, padding: m ? "12px 14px" : "14px 16px", borderRadius: 10, background: "var(--bg-secondary)", border: "1px solid var(--border-light)" },
    infoCellIcon: { fontSize: m ? 20 : 24, flexShrink: 0, width: m ? 28 : 36, textAlign: "center" as const },
    infoCellLabel: { fontSize: 12, color: "var(--text-muted)", marginBottom: 2 },
    infoCellValue: { fontSize: m ? 13 : 14, fontWeight: 600, color: "var(--text-primary)" },

    /* Map */
    exactAddr: { fontSize: m ? 14 : 15, fontWeight: 600, color: "var(--text-primary)", marginBottom: 12, padding: m ? "8px 12px" : "10px 14px", background: "rgba(22,163,74,0.08)", borderRadius: 8, border: "1px solid rgba(22,163,74,0.2)" },
    mapWrap: { position: "relative" as const, borderRadius: 12, overflow: "hidden" as const, border: "1px solid var(--border-light)" },
    map: { width: "100%", height: m ? 260 : 360 },
    mapOverlay: { position: "absolute" as const, inset: 0, background: "rgba(15,23,42,0.45)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center" },
    mapOverlayInner: { textAlign: "center" as const, color: "#fff", fontSize: m ? 14 : 15, fontWeight: 600, lineHeight: 1.8 },
    blindBanner: { position: "absolute" as const, bottom: 0, left: 0, right: 0, padding: m ? "8px 12px" : "10px 16px", background: "rgba(15,23,42,0.8)", backdropFilter: "blur(4px)", borderTop: `1px solid ${gold}`, fontSize: 13, color: "#e2e8f0", lineHeight: 1.5, zIndex: 10 },
    exactBanner: { position: "absolute" as const, bottom: 0, left: 0, right: 0, padding: m ? "6px 12px" : "8px 16px", background: "rgba(22,163,74,0.15)", borderTop: "1px solid rgba(22,163,74,0.3)", fontSize: 13, color: "#16A34A", fontWeight: 600, zIndex: 10 },
    mapCaption: { fontSize: 13, color: "var(--text-muted)", textAlign: "center" as const, marginTop: 8 },

    /* Description */
    descText: { fontSize: m ? 13 : 14, color: "var(--text-secondary)", lineHeight: 1.8, margin: 0, whiteSpace: "pre-wrap" as const },
    noteDivider: { height: 1, background: "var(--border-light)", margin: "16px 0" },
    noteLabel: { fontSize: 13, fontWeight: 700, color: gold, marginBottom: 8 },

    /* Owner */
    ownerCard: { display: "flex", alignItems: "center", gap: m ? 10 : 14, padding: m ? "14px 16px" : "16px 20px", borderRadius: 10, background: "rgba(22,163,74,0.06)", border: "1px solid rgba(22,163,74,0.15)" },
    ownerIcon: { fontSize: m ? 24 : 28 },
    ownerContact: { fontSize: m ? 15 : 16, fontWeight: 600, color: "var(--text-primary)" },

    /* CTA */
    ctaCard: { background: "var(--bg-card)", border: `2px solid ${gold}`, borderRadius: m ? 12 : 16, padding: m ? "18px 14px" : "24px 28px", display: "flex", flexDirection: m ? "column" as const : "row" as const, alignItems: m ? "stretch" : "center", gap: m ? 12 : 16, flexWrap: "wrap" as const },
    ctaIcon: { fontSize: m ? 28 : 36, flexShrink: 0, ...(m && { textAlign: "center" as const }) },
    ctaTextWrap: { flex: 1, minWidth: m ? 0 : 200 },
    ctaTitle: { fontSize: m ? 15 : 16, fontWeight: 700, color: "var(--text-primary)", marginBottom: 4 },
    ctaSub: { fontSize: m ? 12 : 13, color: "var(--text-muted)", lineHeight: 1.5 },
    ctaBtn: { padding: m ? "12px 20px" : "12px 24px", borderRadius: 10, border: "none", background: `linear-gradient(135deg, #B8972E, ${gold})`, color: "#fff", fontSize: m ? 14 : 15, fontWeight: 700, cursor: "pointer", textDecoration: "none",  textAlign: "center" as const, whiteSpace: "nowrap" as const, flexShrink: 0 },

    /* Favorite text button */
    favBtn: { marginTop: m ? 12 : 16, width: "100%", padding: "14px 0", borderRadius: 10, border: "none", background: "linear-gradient(135deg, #d4a843, #b8922e)", color: "#fff", fontSize: 16, fontWeight: 700, cursor: "pointer", transition: "all 0.15s", boxShadow: "0 2px 8px rgba(184,146,46,0.3)", letterSpacing: "0.02em" },
    favBtnActive: { marginTop: m ? 12 : 16, width: "100%", padding: "14px 0", borderRadius: 10, border: "none", background: "#EF4444", color: "#fff", fontSize: 16, fontWeight: 700, cursor: "pointer", transition: "all 0.15s", boxShadow: "0 2px 8px rgba(239,68,68,0.3)", letterSpacing: "0.02em" },

    /* Footer meta */
    footerMeta: { display: "flex", flexWrap: "wrap" as const, gap: m ? 12 : 20, fontSize: m ? 12 : 13, color: "var(--text-muted)", padding: "8px 0" },

    /* Broker card & inquiry */
    brokerCard: { display: "flex", alignItems: "center", gap: m ? 10 : 14, padding: m ? "12px 14px" : "14px 18px", borderRadius: 10, background: "var(--bg-secondary)", border: "1px solid var(--border-light)", marginBottom: m ? 10 : 14 },
    brokerAvatar: { width: m ? 40 : 48, height: m ? 40 : 48, borderRadius: "50%", background: `linear-gradient(135deg, #B8972E, ${gold})`, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: m ? 16 : 18, fontWeight: 700, flexShrink: 0 },
    brokerName: { fontSize: m ? 14 : 15, fontWeight: 700, color: "var(--text-primary)" },
    brokerOffice: { fontSize: m ? 12 : 13, color: "var(--text-muted)", marginTop: 2 },
    inquiryBtns: { display: "flex", gap: m ? 8 : 10 },
    phoneBtn: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: m ? "12px 0" : "13px 0", borderRadius: 10, border: "none", background: "#16A34A", color: "#fff", fontSize: m ? 14 : 15, fontWeight: 700, cursor: "pointer", textDecoration: "none" },
    inquiryBtn: { flex: 1, padding: m ? "12px 0" : "13px 0", borderRadius: 10, border: "none", background: `linear-gradient(135deg, #B8972E, ${gold})`, color: "#fff", fontSize: m ? 14 : 15, fontWeight: 700, cursor: "pointer" },

    /* Lightbox */
    lightbox: { position: "fixed" as const, inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 1000, display: "flex", flexDirection: "column" as const, alignItems: "center", justifyContent: "center" },
    lbClose: { position: "absolute" as const, top: 16, right: 20, background: "none", border: "none", color: "#fff", fontSize: 32, cursor: "pointer" },
    lbImg: { maxWidth: "90vw", maxHeight: "80vh", objectFit: "contain" as const, borderRadius: 8 },
    lbNav: { display: "flex", alignItems: "center", gap: 24, marginTop: 16 },
    lbArrow: { background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", fontSize: 20, width: 44, height: 44, borderRadius: "50%", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" },
  };
}
